import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './HomePage.css';

const HomePage = () => {
    const navigate = useNavigate();
    const [formations, setFormations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const formationsRes = await fetch(`${API_BASE_URL}/douane/v1/formations?per_page=3`);
            const formationsData = await formationsRes.json();
            if (formationsData.success) {
                setFormations(formationsData.formations);
            }
        } catch (err) {
            console.error('Erreur:', err);
        } finally {
            setLoading(false);
        }
    };

    // Scroll reveal observer
    const revealRef = useCallback((node) => {
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('revealed');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
        // Observe all .reveal children
        node.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div className="home-page" ref={revealRef}>

            {/* ====== HERO ====== */}
            <section className="hero">
                <div className="hero-grain" />
                <div className="hero-line" />

                <div className="hero-container">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <span className="hero-badge-dot" />
                            Plateforme N°1 en Afrique de l'Ouest
                        </div>

                        <h1 className="hero-title">
                            Douane &
                            <span className="hero-title-accent">Commerce International</span>
                        </h1>

                        <p className="hero-description">
                            Formations certifiantes, sessions en direct et expertise reconnue.
                            VTYM Consulting Group forme les professionnels de demain
                            en procedures douanieres et commerce international.
                        </p>

                        <div className="hero-actions">
                            <button
                                className="btn-cta-primary"
                                onClick={() => navigate('/formations')}
                            >
                                Decouvrir nos formations
                                <span className="btn-arrow">&rarr;</span>
                            </button>
                            <button
                                className="btn-cta-secondary"
                                onClick={() => navigate('/sessions')}
                            >
                                <span className="btn-live-indicator" />
                                Sessions Live
                            </button>
                        </div>
                    </div>

                    <div className="hero-panel">
                        <div className="stats-card">
                            <div className="stats-card-header">Nos chiffres</div>
                            <div className="stat-row">
                                <div className="stat-value">50+</div>
                                <div className="stat-label">Entreprises formees</div>
                            </div>
                            <div className="stat-row">
                                <div className="stat-value">500+</div>
                                <div className="stat-label">Professionnels certifies</div>
                            </div>
                            <div className="stat-row">
                                <div className="stat-value">8+</div>
                                <div className="stat-label">Annees d'expertise</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hero-scroll">
                    <div className="scroll-line" />
                    <span className="scroll-text">Decouvrir</span>
                </div>
            </section>

            {/* ====== TRUST BANNER ====== */}
            <section className="trust-banner">
                <div className="trust-container">
                    <span className="trust-label">Reconnu par</span>
                    <div className="trust-divider" />
                    <div className="trust-items">
                        <div className="trust-item">
                            <span className="trust-icon">&#127891;</span>
                            Formations Certifiantes
                        </div>
                        <div className="trust-item">
                            <span className="trust-icon">&#127758;</span>
                            Standard International
                        </div>
                        <div className="trust-item">
                            <span className="trust-icon">&#127463;&#127470;</span>
                            Base en Cote d'Ivoire
                        </div>
                    </div>
                </div>
            </section>

            {/* ====== FEATURES ====== */}
            <section className="features-section">
                <div className="features-container">
                    <div className="section-header reveal">
                        <span className="section-eyebrow">Pourquoi VTYM</span>
                        <h2 className="section-heading">
                            Une expertise <span className="section-heading-accent">reconnue</span> au service de votre carriere
                        </h2>
                        <p className="section-description">
                            Des formations concues par des professionnels du terrain, adaptees aux realites du commerce international africain.
                        </p>
                    </div>

                    <div className="features-grid">
                        {[
                            {
                                num: '01',
                                title: 'Formations Certifiantes',
                                text: 'Des programmes reconnus par les professionnels du secteur avec certificat de completion.'
                            },
                            {
                                num: '02',
                                title: 'Sessions en Direct',
                                text: 'Classes virtuelles interactives avec nos experts. Posez vos questions en temps reel.'
                            },
                            {
                                num: '03',
                                title: 'Experts Qualifies',
                                text: 'Formateurs avec plus de 8 ans d\'expertise en douane et commerce international.'
                            },
                            {
                                num: '04',
                                title: 'Accessible 24/7',
                                text: 'Vos cours disponibles depuis n\'importe quel appareil, ou que vous soyez.'
                            },
                            {
                                num: '05',
                                title: 'Paiement Flexible',
                                text: 'Orange Money, Wave ou virement bancaire. La methode qui vous convient.'
                            },
                            {
                                num: '06',
                                title: 'Formations Entreprises',
                                text: 'Tarifs speciaux et programmes sur mesure pour vos equipes.'
                            }
                        ].map((feature, i) => (
                            <div key={i} className={`feature-card reveal reveal-delay-${i % 3 + 1}`}>
                                <div className="feature-number">{feature.num}</div>
                                <h3 className="feature-title">{feature.title}</h3>
                                <p className="feature-text">{feature.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ====== FORMATIONS ====== */}
            <section className="formations-section">
                <div className="formations-container">
                    <div className="formations-header">
                        <div className="section-header reveal">
                            <span className="section-eyebrow">Catalogue</span>
                            <h2 className="section-heading">
                                Nos Formations Phares
                            </h2>
                            <p className="section-description">
                                Des programmes complets pour tous les niveaux
                            </p>
                        </div>
                        <button
                            className="btn-voir-tout reveal"
                            onClick={() => navigate('/formations')}
                        >
                            Voir tout &rarr;
                        </button>
                    </div>

                    {loading ? (
                        <div className="formations-loading">
                            <div className="loading-spinner" />
                            <div>Chargement des formations...</div>
                        </div>
                    ) : formations.length > 0 ? (
                        <div className="formations-grid">
                            {formations.map((formation, i) => (
                                <div
                                    key={formation.id}
                                    className={`formation-card reveal reveal-delay-${i + 1}`}
                                    onClick={() => navigate(`/formations/${formation.id}`)}
                                >
                                    <div className="formation-image-wrapper">
                                        {formation.image_url ? (
                                            <img
                                                src={formation.image_url}
                                                alt={formation.titre}
                                            />
                                        ) : (
                                            <div className="formation-no-image">
                                                <span className="formation-no-image-icon">&#128218;</span>
                                            </div>
                                        )}
                                        <div className="formation-image-overlay" />
                                        {formation.niveau && (
                                            <span className="formation-niveau">
                                                {formation.niveau}
                                            </span>
                                        )}
                                    </div>

                                    <div className="formation-body">
                                        <h3 className="formation-title">{formation.titre}</h3>
                                        <p className="formation-desc">{formation.description}</p>

                                        <div className="formation-meta">
                                            {formation.duree && (
                                                <span className="formation-meta-item">
                                                    &#9201; {formation.duree}
                                                </span>
                                            )}
                                            {formation.niveau && (
                                                <span className="formation-meta-item">
                                                    &#9670; {formation.niveau}
                                                </span>
                                            )}
                                        </div>

                                        <div className="formation-footer">
                                            <div>
                                                <div className="formation-price-label">A partir de</div>
                                                <div>
                                                    <span className="formation-price">
                                                        {Number(formation.prix_particulier).toLocaleString('fr-FR')}
                                                    </span>
                                                    <span className="formation-price-currency">FCFA</span>
                                                </div>
                                            </div>
                                            <button
                                                className="btn-formation"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/formations/${formation.id}`);
                                                }}
                                            >
                                                En savoir plus &rarr;
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="formations-empty reveal">
                            <div className="formations-empty-icon">&#128218;</div>
                            <p>Nos formations seront bientot disponibles.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* ====== CTA ====== */}
            <section className="cta-section">
                <div className="cta-container">
                    <div className="reveal">
                        <div className="cta-eyebrow">Rejoignez-nous</div>
                        <h2 className="cta-heading">
                            Pret a developper vos competences ?
                        </h2>
                        <p className="cta-description">
                            Rejoignez des centaines de professionnels qui ont deja fait confiance
                            a VTYM Consulting Group pour leur formation en douane et commerce international.
                        </p>
                    </div>
                    <div className="cta-actions reveal reveal-delay-2">
                        <button
                            className="btn-cta-dark"
                            onClick={() => navigate('/formations')}
                        >
                            Commencer maintenant &rarr;
                        </button>
                        <button
                            className="btn-cta-outline"
                            onClick={() => navigate('/register')}
                        >
                            Creer un compte
                        </button>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default HomePage;
